import clsx from 'clsx'
import renderInputField from 'components/FormControls/textInput'
import { Icon, Tooltip } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Field, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import * as Yup from 'yup'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import LoginField from 'modules/Forms/Account/Create/subcomponents/LoginField'
import SubmitButtons from 'modules/Forms/Account/Create/subcomponents/submitButtons'
import renderSelect from 'components/FormControls/select'
import formStyles from 'modules/Forms/common-form.module.scss'
import i18n from 'i18next'
import renderMaskedInput from 'components/FormControls/maskedInputField'
import urlValidator from 'utils/validators/urlValidator'

const PER_USER_DAILY_IMPRESSIONS_MIN_LIMIT = 1
const PER_USER_DAILY_IMPRESSIONS_MAX_LIMIT = 10
const DAILY_IMPRESSIONS_MIN_LIMIT = 15000
const DAILY_IMPRESSIONS_MAX_LIMIT = 10000000

export const AdGodSchema = () =>
  Yup.object().shape({
    logins: Yup.array().of(Yup.object()),
    login: Yup.string().when('logins', ([logins], schema) =>
      logins
        ? schema.strip()
        : schema
            .test('baseLoginTest', '', baseLoginValidator)
            .required('common::validation_messages.required'),
    ),
    industry: Yup.string().required('common::validation_messages.required'),
    network: Yup.string().required('common::validation_messages.required'),
    geo: Yup.string().required('common::validation_messages.required'),
    buy_first_impressions_per_user: Yup.boolean().required('common::validation_messages.required'),
    per_user_daily_impressions_limit: Yup.string()
      .test({
        name: 'min_max',
        test: function (value, ctx) {
          const { path } = this

          if (
            +value >= PER_USER_DAILY_IMPRESSIONS_MIN_LIMIT &&
            +value <= PER_USER_DAILY_IMPRESSIONS_MAX_LIMIT
          ) {
            return true
          } else {
            return ctx.createError({
              path,
              message: i18n.t(
                'services::forms.register_account.ad-god.fields.per_user_daily_impressions_limit.errors.min_max',
                {
                  min: PER_USER_DAILY_IMPRESSIONS_MIN_LIMIT,
                  max: PER_USER_DAILY_IMPRESSIONS_MAX_LIMIT,
                },
              ),
            })
          }
        },
      })
      .required('common::validation_messages.required'),
    daily_impressions_limit: Yup.string()
      .test({
        name: 'min_max',
        test: function (value, ctx) {
          const { path } = this

          if (+value >= DAILY_IMPRESSIONS_MIN_LIMIT && +value <= DAILY_IMPRESSIONS_MAX_LIMIT) {
            return true
          } else {
            return ctx.createError({
              path,
              message: i18n.t(
                'services::forms.register_account.ad-god.fields.daily_impressions_limit.errors.min_max',
                {
                  min: DAILY_IMPRESSIONS_MIN_LIMIT,
                  max: DAILY_IMPRESSIONS_MAX_LIMIT,
                },
              ),
            })
          }
        },
      })
      .required('common::validation_messages.required'),
    weekdays: Yup.array().required('common::validation_messages.required'),
    hours: Yup.string().required('common::validation_messages.required'),
    ad_object_url: Yup.string()
      .test('urlValidator', '', urlValidator)
      .required('common::validation_messages.required'),
    video_download_url: Yup.string()
      .test('urlValidator', '', urlValidator)
      .required('common::validation_messages.required'),
  })

let CreateAdGodAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  formHeader,
  form,
  networkId,
  autofill,
  array,
  multiLogins,
}) => {
  const { t } = useTranslation()

  const submitAccountData = useAccountCreateFormSubmit(form, onClose, multiLogins)

  useEffect(() => {
    if (networkId) {
      autofill('network', networkId)
    }
  }, [networkId])

  return (
    <>
      {formHeader}

      <form
        onSubmit={handleSubmit(submitAccountData)}
        noValidate
      >
        <Field
          disabled
          name="network"
          hidden
          fullWidth
          component={renderInputField}
          t={t}
        />
        <div className={'margin-bottom-16'}>
          <LoginField
            form={form}
            multiLogins={multiLogins}
            array={array}
            autofill={autofill}
            networkId={networkId}
            label={'services::forms.register_account.ad-god.fields.login.label'}
          />
        </div>
        <div className={'margin-bottom-16'}>
          <Field
            name="industry"
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.ad-god.fields.industry.label')}
            t={t}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="geo"
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.ad-god.fields.geo.label')}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.ad-god.fields.geo.tooltip')}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'secondary-grey'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="buy_first_impressions_per_user"
            fullWidth
            component={renderSelect}
            options={[
              {
                label: t(
                  'services::forms.register_account.ad-god.fields.buy_first_impressions_per_user.options.yes',
                ),
                value: true,
              },
              {
                label: t(
                  'services::forms.register_account.ad-god.fields.buy_first_impressions_per_user.options.no',
                ),
                value: false,
              },
            ]}
            label={t(
              'services::forms.register_account.ad-god.fields.buy_first_impressions_per_user.label',
            )}
            t={t}
            emptyOptionLabel={t('common::select_empty_option')}
            clearOptionLabel={t('common::select_clear_option')}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t(
                'services::forms.register_account.ad-god.fields.buy_first_impressions_per_user.tooltip',
              )}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'secondary-grey'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="per_user_daily_impressions_limit"
            fullWidth
            component={renderInputField}
            label={t(
              'services::forms.register_account.ad-god.fields.per_user_daily_impressions_limit.label',
            )}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t(
                'services::forms.register_account.ad-god.fields.per_user_daily_impressions_limit.tooltip',
              )}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'secondary-grey'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="daily_impressions_limit"
            type="text"
            fullWidth
            component={renderInputField}
            label={t(
              'services::forms.register_account.ad-god.fields.daily_impressions_limit.label',
            )}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t(
                'services::forms.register_account.ad-god.fields.daily_impressions_limit.tooltip',
              )}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'secondary-grey'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="weekdays"
            fullWidth
            component={renderSelect}
            options={Object.entries(
              t('services::forms.register_account.ad-god.fields.weekdays.options', {
                returnObjects: true,
              }),
            ).map(([id, label]) => ({
              label: label,
              value: id,
            }))}
            multiple
            label={t('services::forms.register_account.ad-god.fields.weekdays.label')}
            t={t}
            emptyOptionLabel={t('common::select_empty_option')}
            clearOptionLabel={t('common::select_clear_option')}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.ad-god.fields.weekdays.tooltip')}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'secondary-grey'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            fullWidth
            // normalize={phoneFieldNormalize}
            name="hours"
            component={renderMaskedInput}
            placeholderChar={'_'}
            placeholder={'ЧЧ:ММ-ЧЧ:ММ'}
            mask={[
              /[0-2]/,
              /[0-9]/,
              ':',
              /[0-5]/,
              /[0-9]/,
              '-',
              /[0-2]/,
              /[0-9]/,
              ':',
              /[0-5]/,
              /[0-9]/,
            ]}
            label={t('services::forms.register_account.ad-god.fields.hours.label')}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.ad-god.fields.hours.tooltip')}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'secondary-grey'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <div className={'margin-bottom-16'}>
          <Field
            name="ad_object_url"
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.ad-god.fields.ad_object_url.label')}
            t={t}
          />
        </div>
        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="video_download_url"
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.ad-god.fields.video_download_url.label')}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.ad-god.fields.video_download_url.tooltip')}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'secondary-grey'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <SubmitButtons
          submitting={submitting}
          onClose={onClose}
        />
      </form>
    </>
  )
}

CreateAdGodAccountForm = reduxForm({
  form: 'CreateAdGodAccountForm',
  initialValues: {
    login: '',
    industry: '',
    geo: '',
    buy_first_impressions_per_user: null,
    per_user_daily_impressions_limit: '',
    daily_impressions_limit: '',
    weekdays: null,
    hours: '',
    ad_object_url: '',
    video_download_url: '',
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateAdGodAccountForm)

CreateAdGodAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  autofill: PropTypes.func,
  networkId: PropTypes.string,
  networkType: PropTypes.string,
  initialValues: PropTypes.object,
  form: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
  formHeader: PropTypes.node,
}

export default CreateAdGodAccountForm
